import { addDays, addMonths, format, getDay, isAfter, isBefore, subMonths } from "date-fns";

// Congé de naissance et congé de paternité d'un salarié du privé
// (service-public.gouv.fr F2266 et F3156, ameli.fr) :
// - congé de naissance : 3 jours ouvrables, à partir du jour de la naissance ou
//   du 1er jour ouvrable suivant, payés par l'employeur ;
// - congé de paternité : 25 jours calendaires (32 pour une naissance multiple),
//   dont 4 jours obligatoires immédiatement après le congé de naissance ; le
//   reste (21 ou 28 jours) en 1 ou 2 périodes d'au moins 5 jours, dans les 6 mois
//   suivant la naissance ; jusqu'à 30 jours de plus si l'enfant est hospitalisé
//   dès sa naissance.

export const MANDATORY_DAYS = 4;
export const MIN_PERIOD = 5;
export const MAX_HOSPITAL_DAYS = 30;
export const paterniteDays = (multiple) => (multiple ? 32 : 25);

// Pâques (algorithme grégorien anonyme), pour les jours fériés mobiles.
function easter(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

// Jours fériés nationaux (hors Alsace-Moselle).
export function holidays(year) {
  const e = easter(year);
  return new Set([
    `${year}-01-01`, `${year}-05-01`, `${year}-05-08`, `${year}-07-14`,
    `${year}-08-15`, `${year}-11-01`, `${year}-11-11`, `${year}-12-25`,
    format(addDays(e, 1), "yyyy-MM-dd"), // lundi de Pâques
    format(addDays(e, 39), "yyyy-MM-dd"), // Ascension
    format(addDays(e, 50), "yyyy-MM-dd"), // lundi de Pentecôte
  ]);
}

// Jour ouvrable : tous les jours sauf le dimanche et les jours fériés.
export function isOuvrable(date) {
  return getDay(date) !== 0 && !holidays(date.getFullYear()).has(format(date, "yyyy-MM-dd"));
}

function nextOuvrable(date) {
  let d = date;
  while (!isOuvrable(d)) d = addDays(d, 1);
  return d;
}

// start: "birth" (le jour de la naissance, ou le 1er jour ouvrable s'il ne l'est
// pas) ou "next" (le 1er jour ouvrable suivant la naissance).
export function congeNaissance(birth, start = "birth") {
  let d = nextOuvrable(start === "next" ? addDays(birth, 1) : birth);
  const first = d;
  let count = 1;
  while (count < 3) { d = addDays(d, 1); if (isOuvrable(d)) count++; }
  return { start: first, end: d };
}

const period = (start, days) => ({ start, end: addDays(start, days - 1), days });

// plan: { mode: "immediate" | "later" | "split", p1Start, p1Days, p2Start } (dates).
export function plan({ birth, multiple = false, naissanceStart = "birth", hospitalDays = 0, mode = "immediate", p1Start, p1Days, p2Start }) {
  const naissance = congeNaissance(birth, naissanceStart);
  const mandatory = period(addDays(naissance.end, 1), MANDATORY_DAYS);
  const hosp = Math.max(0, Math.min(MAX_HOSPITAL_DAYS, Math.round(hospitalDays) || 0));
  const hospital = hosp > 0 ? period(addDays(mandatory.end, 1), hosp) : null;
  const afterMandatory = addDays((hospital || mandatory).end, 1);
  const remaining = paterniteDays(multiple) - MANDATORY_DAYS;
  const deadline = addMonths(birth, 6);

  let periods = [];
  const errors = [];
  if (mode === "immediate") {
    periods = [period(afterMandatory, remaining)];
  } else if (mode === "later") {
    if (p1Start) periods = [period(p1Start, remaining)];
  } else if (mode === "split") {
    const d1 = Math.round(p1Days) || 0;
    if (d1 < MIN_PERIOD || remaining - d1 < MIN_PERIOD) {
      errors.push(`Chaque période doit compter au moins ${MIN_PERIOD} jours : la 1re période doit faire entre ${MIN_PERIOD} et ${remaining - MIN_PERIOD} jours.`);
    }
    if (p1Start && p2Start) periods = [period(p1Start, d1), period(p2Start, remaining - d1)];
  }

  periods.forEach((p, i) => {
    if (isBefore(p.start, afterMandatory)) errors.push(`La période ${i + 1} ne peut pas commencer avant le ${format(afterMandatory, "dd/MM/yyyy")}.`);
    if (isAfter(p.end, deadline)) errors.push(`La période ${i + 1} dépasse le délai de 6 mois : elle doit se terminer au plus tard le ${format(deadline, "dd/MM/yyyy")}.`);
  });
  if (periods.length === 2 && !isAfter(periods[1].start, periods[0].end)) errors.push("La 2e période doit commencer après la fin de la 1re.");

  return {
    naissance,
    mandatory,
    hospital,
    periods,
    remaining,
    deadline,
    errors,
    // Délai de prévenance : 1 mois avant le début de chaque période optionnelle.
    notices: periods.map((p) => subMonths(p.start, 1)),
    totalPaternite: paterniteDays(multiple) + hosp,
    // Jours de congé : 3 jours de naissance + jours de paternité. La période de
    // naissance peut s'étaler sur plus de 3 jours calendaires (dimanche, férié).
    totalLeave: 3 + paterniteDays(multiple) + hosp,
  };
}
